// HarfBuzz shaping in the browser: the same shaper and font files as the server, so the canvas,
// agent screenshots and exports agree glyph for glyph (see core/shaper.ts).
import { HarfBuzzShaper, setTextShaper, setFeatureFontResolver, invalidateTextMetrics, toSfnt, interFile, INTER_WEIGHTS, type FontProvider, type FontStyleInfo } from '@vellum/core';
import { app } from './app.ts';
import { libraryFonts } from './customFonts.ts';
import { localFontBytes, localStyles } from './localFonts.ts';

// Inter 3.19's WOFF files, the version Figma ships (Brotli-compressed WOFF2 can't be decoded in
// every browser; WOFF uses zlib)
const files = import.meta.glob('../../../node_modules/inter-ui/Inter ?web?/Inter-*.woff', { query: '?url', import: 'default', eager: true }) as Record<string, string>;
const inter = new Map<string, string[]>(); // "weight|italic" → URL
for (const w of INTER_WEIGHTS)
  for (const italic of [false, true]) {
    const url = Object.entries(files).find(([p]) => p.endsWith(`/${interFile(w, italic)}.woff`))?.[1];
    if (url) inter.set(`${w}|${italic}`, [url]);
  }
const interStyles: FontStyleInfo[] = [...inter.keys()].map((k) => ({ weight: Number(k.split('|')[0]), italic: k.endsWith('true') }));

async function inflate(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function fetchFont(url: string): Promise<Uint8Array> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return toSfnt(new Uint8Array(await r.arrayBuffer()), { inflate });
}

const provider: FontProvider = {
  styles(family) {
    const k = family.toLowerCase();
    // uploaded fonts first: a team may upload its own build of a family
    const lib = libraryFonts.filter((f) => f.family.toLowerCase() === k && f.sfnt);
    if (lib.length) return lib.map((f) => ({ weight: f.weight, italic: f.italic }));
    if (k === 'inter') return interStyles;
    // then fonts installed on this computer
    return localStyles(family);
  },
  load(family, weight, italic) {
    const k = family.toLowerCase();
    const lib = libraryFonts.find((f) => f.family.toLowerCase() === k && f.weight === weight && f.italic === italic && f.sfnt);
    if (lib) return fetchFont(lib.sfnt!).then((b) => [b]);
    if (k !== 'inter') {
      const local = localFontBytes(family, weight, italic);
      if (local) return local.then((b) => [b]);
    }
    return Promise.all((inter.get(`${weight}|${italic}`) ?? []).filter(Boolean).map(fetchFont));
  },
};

let shaper: HarfBuzzShaper | null = null;

/** Load HarfBuzz and the most used Inter styles; other fonts load on first use. */
export async function installShaping(): Promise<void> {
  try {
    const hb = await import('harfbuzzjs');
    shaper = new HarfBuzzShaper(hb, provider, () => {
      invalidateTextMetrics();
      app.fontsReady();
    });
    // warm the common styles so the first layout is already shaped
    const s = shaper;
    await Promise.race([Promise.all([400, 500, 600, 700].map((w) => s.preload('Inter', w, false))), new Promise((r) => setTimeout(r, 4000))]);
    setTextShaper(shaper);
    // features come from the shaper now, not from per-feature browser font faces
    setFeatureFontResolver(null);
  } catch (e) {
    // the canvas's own text engine keeps working
    console.warn('[vellum] HarfBuzz shaping unavailable', e);
  }
}

/** New uploaded fonts: shape with them from now on. */
export function fontsChanged() {
  shaper?.invalidate();
  invalidateTextMetrics();
}
